/**
 * BHG Design Tokens - Featured Icons
 *
 * Icon tokens for featured icons used throughout the application.
 * Uses Tabler Icons (@tabler/icons-react) with our design system colors.
 */

import { colors } from './colors';

/**
 * Icon types available in the system.
 *
 * When adding a new type: also wire Tabler in `Icon.tsx` and, if it should appear on
 * site announcement banners, add it to `lambda/shared/announcementIcons.ts` (`ANNOUNCEMENT_ICON_REGISTRY`).
 * See `.cursor/rules/tabler-icons-announcements.mdc`.
 */
export type IconType =
  | 'star'
  | 'exclamation-circle'
  | 'info-circle'
  | 'message-report'
  | 'exclamation-triangle'
  | 'checkmark'
  | 'chevron-up'
  | 'chevron-left'
  | 'circle-plus'
  | 'book'
  | 'chalkboard-teacher'
  | 'smart-home'
  | 'users'
  | 'clipboard-list'
  | 'device-ipad-horizontal-search'
  | 'currency-dollar'
  | 'credit-card'
  | 'credit-card-pay'
  | 'brand-paypal'
  | 'brand-gmail'
  | 'calendar'
  | 'calendar-event'
  | 'file-text'
  | 'file-analytics'
  | 'chart-line'
  | 'file-percent'
  | 'report-medical'
  | 'report-money'
  | 'shield-check'
  | 'user'
  | 'user-plus'
  | 'map-pin'
  | 'map-search'
  | 'eye'
  | 'eye-off'
  | 'mail'
  | 'mail-check'
  | 'mail-x'
  | 'user-check'
  | 'phone'
  | 'phone-check'
  | 'phone-x'
  | 'shield'
  | 'circle-dashed-check'
  | 'progress-check'
  | 'circle-check'
  | 'circle-minus'
  | 'circle-x'
  | 'qrcode'
  | 'device-mobile-message'
  | 'device-desktop-check'
  | 'device-tablet-check'
  | 'logout-2'
  | 'refresh'
  | 'refresh-alert'
  | 'download'
  | 'trash'
  | 'photo'
  | 'users-group'
  | 'coin'
  | 'coin-off'
  | 'baby-carriage'
  | 'old'
  | 'bold'
  | 'italic'
  | 'blockquote'
  | 'link'
  | 'thumb-up'
  | 'mood-smile'
  | 'ban'
  | 'bell'
  | 'bell-check'
  | 'bell-ringing'
  | 'adjustments-horizontal'
  | 'search'
  | 'copy'
  | 'school'
  | 'clock'
  | 'clock-hour-3'
  | 'hourglass-low'
  | 'sunrise'
  | 'sun'
  | 'sunglasses'
  | 'sunset'
  | 'moon'
  | 'notes'
  | 'backpack'
  | 'traffic-cone'
  | 'cloud'
  | 'cloud-rain'
  | 'cloud-snow'
  | 'cloud-storm'
  | 'wind'
  | 'snowflake'
  | 'barrier-block'
  | 'building-church'
  | 'buildings'
  | 'mailbox'
  | 'phone-call'
  | 'bible'
  | 'heart-handshake'
  | 'cross'
  | 'confetti'
  | 'balloon'
  | 'award'
  | 'leaf-maple'
  | 'christmas-tree'
  | 'fish-christianity'
  | 'pray'
  | 'tools-kitchen-2'
  | 'grid-dots'
  | 'x';

/**
 * Icon style variants
 */
export type IconStyle = 'outlined' | 'filled';

/**
 * Background shape variants
 */
export type IconBackgroundShape = 'circle' | 'rounded-square';

/**
 * Icon color variants mapped to design tokens
 */
export type IconColorVariant =
  | 'blue'
  | 'gray'
  | 'red'
  | 'orange'
  | 'green'
  | 'secondary';

/**
 * Icon sizes
 */
export type IconSize = 'sm' | 'md' | 'lg' | 'xl';

/**
 * Icon size values in pixels
 */
export const iconSizes = {
  sm: 16,
  md: 24,
  lg: 32,
  xl: 48,
} as const;

/**
 * Icon color mappings to design tokens
 * Light variants for outlined style, dark variants for filled style
 */
export const iconColors = {
  // Light variants (for outlined icons with subtle backgrounds)
  light: {
    blue: colors.primary[100], // Light blue
    gray: colors.gray[200], // Light gray
    red: colors.error[100], // Light red/pink
    orange: colors.warning[100], // Light orange
    green: colors.success[100], // Light green
    secondary: colors.secondary[100], // Light gold
  },
  // Dark variants (for filled icons with solid backgrounds)
  dark: {
    blue: colors.primary[500], // Dark blue
    gray: colors.gray[600], // Dark gray
    red: colors.error[500], // Red
    orange: colors.warning[500], // Orange
    green: colors.success[500], // Green
    secondary: colors.secondary[600], // Gold
  },
  // Icon fill colors (for filled style icons)
  icon: {
    outlined: colors.gray[700], // Dark gray for outlined icons
    filled: colors.base.white, // White for filled icons on colored backgrounds
  },
} as const;

/**
 * Background fill opacity for outlined style
 */
export const iconBackgroundOpacity = {
  outlined: 0.1, // Subtle fill for outlined icons
  filled: 1.0, // Solid fill for filled icons
} as const;

/**
 * Border radius values for background shapes
 */
export const iconBorderRadius = {
  circle: '50%',
  'rounded-square': '8px',
} as const;

/**
 * Icon configuration interface
 */
export interface IconConfig {
  /** Icon type */
  type: IconType;
  /** Icon style */
  style: IconStyle;
  /** Background shape */
  backgroundShape: IconBackgroundShape;
  /** Color variant */
  colorVariant: IconColorVariant;
  /** Size */
  size: IconSize;
  /** Background color (calculated) */
  backgroundColor: string;
  /** Icon color (calculated) */
  iconColor: string;
  /** Border radius */
  borderRadius: string;
}

/**
 * Get icon configuration
 */
export const getIconConfig = (
  type: IconType,
  style: IconStyle,
  backgroundShape: IconBackgroundShape,
  colorVariant: IconColorVariant,
  size: IconSize = 'md'
): IconConfig => {
  const isOutlined = style === 'outlined';
  const colorPalette = isOutlined ? iconColors.light : iconColors.dark;
  const backgroundColor = colorPalette[colorVariant];
  const iconColor = isOutlined ? iconColors.dark[colorVariant] : iconColors.icon[style];
  const borderRadius = iconBorderRadius[backgroundShape];

  return {
    type,
    style,
    backgroundShape,
    colorVariant,
    size,
    backgroundColor,
    iconColor,
    borderRadius,
  };
};

/**
 * Predefined icon configurations for common use cases
 */
export const iconPresets = {
  // Featured/Favorite (Star)
  featured: {
    light: {
      blue: getIconConfig('star', 'outlined', 'circle', 'blue'),
      gray: getIconConfig('star', 'outlined', 'circle', 'gray'),
    },
    dark: {
      blue: getIconConfig('star', 'filled', 'circle', 'blue'),
      gray: getIconConfig('star', 'filled', 'circle', 'gray'),
    },
  },

  // Warning/Error (Exclamation)
  warning: {
    circle: {
      light: getIconConfig('exclamation-circle', 'outlined', 'circle', 'red'),
      dark: getIconConfig('exclamation-circle', 'filled', 'circle', 'red'),
    },
    triangle: {
      light: getIconConfig('exclamation-triangle', 'outlined', 'circle', 'orange'),
      dark: getIconConfig('exclamation-triangle', 'filled', 'circle', 'orange'),
    },
  },

  // Success (Checkmark)
  success: {
    light: {
      blue: getIconConfig('checkmark', 'outlined', 'circle', 'blue'),
      gray: getIconConfig('checkmark', 'outlined', 'circle', 'gray'),
      red: getIconConfig('checkmark', 'outlined', 'circle', 'red'),
      orange: getIconConfig('checkmark', 'outlined', 'circle', 'orange'),
      green: getIconConfig('checkmark', 'outlined', 'circle', 'green'),
    },
    dark: {
      blue: getIconConfig('checkmark', 'filled', 'circle', 'blue'),
      gray: getIconConfig('checkmark', 'filled', 'circle', 'gray'),
      red: getIconConfig('checkmark', 'filled', 'circle', 'red'),
      orange: getIconConfig('checkmark', 'filled', 'circle', 'orange'),
      green: getIconConfig('checkmark', 'filled', 'circle', 'green'),
    },
  },
} as const;
